import { get } from 'lodash';
import getMetricsData from '@api/metricContainer';
import instance from '@api/axiosInstance';
import useEventMetricsQuery, { createEventMetricsQuery } from './useEventMetricsQuery';
import useQueryParams from '../../hooks/useQueryParams';
import { formatTimeseriesData } from '@src/components/metrics-panel-hz/utils/format';
import { panelsAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { EVENT_METRICS_LIST } from '@src/hooks/event/useEventConfigs';
import { calcDodInterval } from '../../utils';

/**
 * Hook to fetch DoD (Day-over-Day) metrics for a specific event
 * Uses signal_name_param instead of event filter
 */
function useFetchDodEventMetricsData({
  eventName,
  eventMeta,
  enabled = false,
}: {
  eventName: string;
  eventMeta?: any;
  enabled?: boolean;
}) {
  const panels = useAtomValue(panelsAtom);
  const { interval, filter, _rKey } = useQueryParams();

  const isDeleted = eventMeta?.isDeleted;

  const dodInterval = calcDodInterval(interval);

  // Use base filter only (no event filter)
  const options = {};
  const payload = dodInterval
    ? createEventMetricsQuery(
        eventName,
        `${dodInterval.from}/${dodInterval.to}`,
        dodInterval.granularity,
        filter || [],
        options,
      )
    : null;

  const result = useEventMetricsQuery({
    eventName: `${eventName}-dod`,
    queryKey: ['overview', 'event-dod', eventName, payload?.queries, _rKey],
    queryFn: () =>
      payload && dodInterval
        ? getMetricsData(payload, {}, instance).then((response) => {
            const { data, totals, annotations } = get(response, ['data', '0'], {
              totals: {},
              data: [],
              annotations: [],
            });
            const formattedData = formatTimeseriesData({
              metrics: EVENT_METRICS_LIST,
              data,
              interval: dodInterval,
              annotations,
            });
            return { data: formattedData, totals };
          })
        : Promise.resolve({ data: {}, totals: {} }),
    options: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      enabled: enabled && !isDeleted && panels.events && !!dodInterval,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5m
    },
  });

  if (isDeleted || !enabled || !dodInterval) {
    return {
      data: null,
      isLoading: false,
      isFetching: false,
      isError: false,
      error: null,
      refetch: () => Promise.resolve(),
      queued: false,
    };
  }

  return result;
}

export default useFetchDodEventMetricsData;
